import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAuditLog } from '@/hooks/useAuditLog';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

type ScopeType = 'global' | 'personal';

export const useProjects = (scope: ScopeType) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['projects', scope],
    enabled: !!user,
    queryFn: async () => {
      let query = supabase.from('projects').select('*');
      if (scope === 'global') query = query.eq('scope_type', 'global');
      else if (scope === 'personal') query = query.eq('scope_type', 'personal').eq('owner_id', user!.id);
      query = query.order('updated_at', { ascending: false });
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
};

/** Projetos compartilhados comigo: via project_members OU via project_team_members (excluindo projetos que sou dono) */
export const useSharedProjects = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['projects', 'shared'],
    enabled: !!user,
    queryFn: async () => {
      // 1. Projetos onde sou membro individual
      const { data: directMemberships, error: mError } = await supabase
        .from('project_members')
        .select('project_id')
        .eq('user_id', user!.id);
      if (mError) throw mError;

      // 2. Projetos onde algum time meu tem acesso
      const { data: teamMemberships, error: tError } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('user_id', user!.id);
      if (tError) throw tError;

      const teamIds = (teamMemberships ?? []).map(t => t.team_id);
      let teamProjectIds: string[] = [];
      if (teamIds.length > 0) {
        const { data: ptm, error: ptmError } = await supabase
          .from('project_team_members')
          .select('project_id')
          .in('team_id', teamIds);
        if (ptmError) throw ptmError;
        teamProjectIds = (ptm ?? []).map(p => p.project_id);
      }

      const allIds = Array.from(new Set([
        ...(directMemberships ?? []).map(m => m.project_id),
        ...teamProjectIds,
      ]));

      if (!allIds.length) return [];

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .in('id', allIds)
        .neq('owner_id', user!.id)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async (project: TablesInsert<'projects'>) => {
      const { data, error } = await supabase.from('projects').insert(project).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      logAction({ action: 'create_project', targetType: 'project', targetId: data.id, metadata: { name: data.name } });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async ({ id, ...updates }: TablesUpdate<'projects'> & { id: string }) => {
      const { data, error } = await supabase.from('projects').update(updates).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      logAction({ action: 'update_project', targetType: 'project', targetId: data.id });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  const { logAction } = useAuditLog();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('projects').delete().eq('id', id).select('id').single();
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      logAction({ action: 'delete_project', targetType: 'project', targetId: id });
    },
  });
};
